import { useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
  UncontrolledDropdown,
  Container,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  Alert,
  Badge,
} from "reactstrap";
import { Link, Navigate, useParams } from "react-router-dom";
import AppHelmet from "../../../components/AppComponents/AppHelmet";
import API from "../../../api/api/API";
import { AppConfirm } from "../../../utils/PopupUtil";
import { newToastEvent } from "../../../utils/ToastUtil";
import ModifyVariableValueModal from "../../../components/Inputs/ModifyVariableValueModal";
import ImportCSVModal from "../../../components/Modals/ImportCSVModal";
import BasicTableView from "../CloudListView/BasicTableView";
import IconButton from "../../../elements/IconButton";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ExportGlobalListDrawer from "./ExportGlobalListDrawer";

// Define types for Props and State
interface Variable {
  Name: string;
  Type: string;
  Value: any;
  id: string;
}

export interface List {
  Name: string;
  APIName: string;
  Variables: Variable[];
}

function useGlobalList(listAPIName: string) {
  const globalList = useQuery<List>({
    queryKey: ["globalList", listAPIName],
    queryFn: () => {
      return API.getGlobalVariableListByID(listAPIName).then(
        (data) => {
          return data.data.data.list;
        },
        (err) => {
          console.error(err);
        }
      );
    },
  });

  return { list: globalList.data, loading: globalList.isLoading };
}

const GlobalVariableListDetailView = ({ urlStart }: { urlStart?: string }) => {
  const [submitting, setSubmitting] = useState(false);
  const [selectedListValue, setSelectedListValue] = useState<Variable | null>(
    null
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [importerOpen, setImporterOpen] = useState(false);
  const [importerError, setImporterError] = useState<string | null>(null);
  const [redirect, setRedirect] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportDrawer, setExportDrawer] = useState(false);

  const { listID } = useParams<{ listID: string }>();
  const { list, loading } = useGlobalList(listID!);
  const client = useQueryClient();
  urlStart = urlStart || "/supportal";

  const toggleModal = () => setModalOpen(!modalOpen);

  const toggleImporter = () => setImporterOpen(!importerOpen);

  const setErrorState = (name: string, val: string | null = null) => {
    setError(val);

    if (val) {
      setTimeout(() => {
        setError(null);
      }, 4000);
    }
  };

  const checkForMixedTypes = (list: List | undefined): boolean => {
    if (!list) return false;

    let originalType = null;

    for (let i = 0; i < list.Variables.length; i++) {
      if (i === 0) {
        originalType = typeof list.Variables[i].Value;
      } else if (typeof list.Variables[i].Value !== originalType) {
        return true;
      }
    }
    return false;
  };

  const handleUpdate = (newValues: Variable) => {
    if (selectedListValue) {
      modifyValue(newValues);
    } else {
      createValue(newValues);
    }
  };

  const verifyValues = (values: Variable, stateError = "error"): boolean => {
    let value = values.Value;
    const type = values.Type;

    if (type === "int") {
      value = parseInt(value, 10);

      if (isNaN(value)) {
        setErrorState(
          stateError,
          "The integer value you entered is not a number."
        );
        return false;
      }
    } else if (type === "float") {
      value = parseFloat(value);

      if (isNaN(value)) {
        setErrorState(
          stateError,
          "The float value you entered is not a number."
        );
        return false;
      }
    } else if (type === "object") {
      try {
        value = JSON.parse(value);
      } catch (e) {
        setErrorState(stateError, "The JSON object you entered is invalid.");
        return false;
      }
    }

    return true;
  };

  const modifyValue = (values: Variable) => {
    if (!verifyValues(values)) return;

    setSubmitting(true);

    API.modifyGlobalVariableListValue(
      list!.APIName,
      selectedListValue!.id,
      values
    )
      .then(
        (data) => {
          client.setQueryData(
            ["globalList", listID],
            data.data.data.updatedList
          );
          toggleModal();
        },
        (error) => {
          if (error.response && error.response.status === 409) {
            setErrorState(
              "error",
              "A variable with that Name already exists. Modify the Name to continue."
            );
          } else {
            setErrorState(
              "error",
              "We were unable to modify the variable. Please try again."
            );
          }
          console.error(error);
        }
      )
      .finally(() => setSubmitting(false));
  };

  const deleteValue = (f: Variable) => {
    AppConfirm({
      title: `Delete ${f.Name} Value?`,
      text: `Are you sure you want to delete the ${f.Name} value. This cannot be undone.`,
    }).then((result) => {
      if (!result.isConfirmed) return;

      API.deleteGlobalVariableListValue(list!.APIName, f.id).then(
        (data) => {
          client.setQueryData(
            ["globalList", listID],
            data.data.data.updatedList
          );
          newToastEvent(
            "success",
            `The ${f.Name} value was successfully deleted.`
          );
        },
        (error) => {
          newToastEvent(
            "error",
            "We were unable to delete the value. Please try again."
          );
          console.error(error);
        }
      );
    });
  };

  const deleteList = () => {
    AppConfirm({
      title: `Delete ${list?.Name} List?`,
      text: `Are you sure you want to delete the ${list?.Name} Variable List. This cannot be undone.`,
    }).then((result) => {
      if (!result.isConfirmed) return;

      API.deleteGlobalVariableList(list!.APIName).then(
        () => {
          AppConfirm({
            title: `List Deleted`,
            text: `The variable list has been successfully deleted.`,
            icon: "success",
            showCancelButton: false,
            confirmButtonText: "OK",
            confirmButtonColor: "var(--primary)",
          }).then(() => {
            setRedirect(true);
          });
        },
        (error) => {
          newToastEvent(
            "error",
            "We were unable to delete the variable list. Please try again."
          );
          console.error(error);
        }
      );
    });
  };

  const handleUpdatedList = (
    dbCall: Promise<any>,
    toggleFunction: () => void,
    errorName: string
  ) => {
    dbCall
      .then(
        (data) => {
          client.setQueryData(
            ["globalList", listID],
            data.data.data.updatedList
          );
          newToastEvent("success", "The value was successfully added.");
          toggleFunction();
        },
        (error) => {
          if (error.response && error.response.status === 409) {
            setErrorState(
              errorName,
              "A value with that Name already exists. Modify the Name to continue."
            );
          } else {
            setErrorState(
              errorName,
              "We were unable to create the value. Please try again."
            );
          }
          console.error(error);
        }
      )
      .finally(() => {
        setSubmitting(false);
      });
  };

  const createValue = (values: Variable) => {
    if (!verifyValues(values)) return;

    setSubmitting(true);

    handleUpdatedList(
      API.addGlobalVariableListValue(list!.APIName, values),
      toggleModal,
      "error"
    );
  };

  const createValues = (valuesArray: Variable[]) => {
    handleUpdatedList(
      API.addGlobalVariableListValues(list!.APIName, valuesArray),
      toggleImporter,
      "importerError"
    );
  };

  const importCsvData = (data: Variable[]) => {
    setImporterError(null);

    for (const item of data) {
      if (!verifyValues(item, "importerError")) {
        return;
      }
    }

    createValues(data);
  };

  if (redirect) {
    return <Navigate to={`${urlStart}/global/variables/lists`} replace />;
  }

  return (
    <>
      <AppHelmet title="Variable List - GrowthCloud.com" />
      <div className="header pb-6">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center py-4">
              <Col xs="">
                <h6 className="h2 mb-0">{list?.Name || listID}</h6>{" "}
                <Breadcrumb
                  className="d-none d-md-block mt-1"
                  listClassName="breadcrumb-links"
                >
                  <BreadcrumbItem>
                    <Link to={urlStart}>
                      <i className="mdi mdi-rocket" />
                    </Link>
                  </BreadcrumbItem>
                  <BreadcrumbItem>
                    <Link to={`${urlStart}/global/variables/lists`}>
                      Global Variables
                    </Link>
                  </BreadcrumbItem>
                  <BreadcrumbItem>
                    <Link to={`${urlStart}/global/variables/lists`}>Lists</Link>
                  </BreadcrumbItem>
                  <BreadcrumbItem>{list?.Name || listID}</BreadcrumbItem>
                </Breadcrumb>
              </Col>
              <Col className="text-lg-right mt-0" xs="auto">
                <UncontrolledDropdown>
                  <DropdownToggle className="btn-icon btn-outline-secondary">
                    <span className="btn-inner--text">Actions</span>
                    <span className="btn-inner--icon">
                      <i
                        className="mdi mdi-chevron-down"
                        style={{ position: "relative", top: "1px" }}
                      />
                    </span>
                  </DropdownToggle>
                  <DropdownMenu
                    className="dropdown-menu-arrow btn-oauth"
                    style={{ zIndex: 999 }}
                    right
                  >
                    <DropdownItem
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleImporter();
                      }}
                    >
                      Import Items from CSV
                    </DropdownItem>
                    <DropdownItem
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setExportDrawer(true);
                      }}
                    >
                      Download JSON
                    </DropdownItem>
                    <DropdownItem
                      className="text-danger"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        deleteList();
                      }}
                    >
                      Delete
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
      <Container className="mt--6" fluid>
        <hr className="mt-0 mb-4"></hr>
        <Row>
          <Col xs="12">
            {checkForMixedTypes(list) && (
              <Alert color="superlight">
                <strong>Heads Up!</strong> Your variable list contains values
                with different data types. If this is intended, you can ignore
                this notice.
              </Alert>
            )}
          </Col>
          <Col xs="12">
            <Card className="shadow">
              <CardHeader className="bg-superlight font-weight-600 text-dark btn-oauth py-2">
                <Row className="align-items-center">
                  <Col>
                    <h6 className="h3 mb-0">List Values</h6>
                  </Col>
                  <Col xs="auto" className="text-right">
                    <Button
                      color="primary"
                      size="sm"
                      className="px-4"
                      onClick={() => {
                        toggleModal();
                        setSelectedListValue(null);
                      }}
                    >
                      Add
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <BasicTableView
                items={list?.Variables || []}
                loading={loading}
                getItemTitle={(i) => i.Name}
                getItemSubtitle={(i) => {
                  return (
                    <div className="d-flex align-items-center">
                      <Badge color="primary">{i.Type}</Badge>
                      <div className="mx-2 font-weight-500">{i.Value}</div>
                    </div>
                  );
                }}
                CustomRenderer={(f) => {
                  return (
                    <>
                      <IconButton
                        icon="pencil"
                        onClick={() => {
                          setSelectedListValue(f);
                          toggleModal();
                        }}
                      />
                      <IconButton
                        icon="delete"
                        color="danger"
                        onClick={() => deleteValue(f)}
                      />
                    </>
                  );
                }}
                title={undefined}
              />
            </Card>
          </Col>
        </Row>
      </Container>
      <ModifyVariableValueModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        submitting={submitting}
        error={error}
        // @ts-ignore
        listValue={selectedListValue}
        listName={list?.Name}
        onSave={handleUpdate}
        startingType={list?.Variables?.[0]?.Type}
      />
      <ImportCSVModal
        open={importerOpen}
        onClose={toggleImporter}
        importData={importCsvData}
        submitting={submitting}
        error={importerError}
      />
      <ExportGlobalListDrawer
        open={exportDrawer}
        onClose={() => setExportDrawer(false)}
        list={list}
      />
    </>
  );
};

export default GlobalVariableListDetailView;
